import React, { useState } from 'react';
import { ExperienceLevel } from '@skyment-org/shared-lib';

export interface MenteeOnboardingData {
  experienceLevel: ExperienceLevel;
  currentJobTitle?: string;
  currentCompany?: string;
  skills: string[];
  desiredSkills: string[];
  industries: string[];
  careerGoals: string[];
  isLookingForMentor: boolean;
  isWillingToPayForSessions: boolean;
  budgetForPaidSessions?: number;
  bio: string;
  location: string;
  learningStyle?: string;
  challenges?: string[];
}

interface MenteeOnboardingFormProps {
  onSubmit: (data: MenteeOnboardingData) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const MenteeOnboardingForm: React.FC<MenteeOnboardingFormProps> = ({
  onSubmit,
  onBack,
  isLoading = false
}) => {
  const [formData, setFormData] = useState<MenteeOnboardingData>({
    experienceLevel: ExperienceLevel.ENTRY,
    currentJobTitle: '',
    currentCompany: '',
    skills: [],
    desiredSkills: [],
    industries: [],
    careerGoals: [],
    isLookingForMentor: true,
    isWillingToPayForSessions: false,
    bio: '',
    location: '',
    learningStyle: '',
    challenges: []
  });

  const [skillInput, setSkillInput] = useState('');
  const [desiredSkillInput, setDesiredSkillInput] = useState('');
  const [industryInput, setIndustryInput] = useState('');
  const [goalInput, setGoalInput] = useState('');
  const [challengeInput, setChallengeInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error when field is updated
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleAddItem = (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: string,
    setValue: React.Dispatch<React.SetStateAction<string>>,
    field: keyof MenteeOnboardingData
  ) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault();
      const currentArray = formData[field] as string[];
      if (!currentArray.includes(value.trim())) {
        setFormData(prev => ({
          ...prev,
          [field]: [...currentArray, value.trim()]
        }));
      }
      setValue('');
    }
  };

  const handleRemoveItem = (item: string, field: keyof MenteeOnboardingData) => {
    const currentArray = formData[field] as string[];
    setFormData(prev => ({
      ...prev,
      [field]: currentArray.filter(i => i !== item)
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.skills.length === 0) {
      newErrors.skills = 'At least one current skill is required';
    }

    if (formData.desiredSkills.length === 0) {
      newErrors.desiredSkills = 'At least one desired skill is required';
    }

    if (formData.careerGoals.length === 0) {
      newErrors.careerGoals = 'At least one career goal is required';
    }

    if (!formData.bio.trim()) {
      newErrors.bio = 'Bio is required';
    }

    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit(formData);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Mentee Profile</h1>
        <p className="text-gray-600">
          Tell us about yourself so we can match you with the right mentors.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Experience Level */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Experience Level *
          </label>
          <select
            name="experienceLevel"
            value={formData.experienceLevel}
            onChange={handleInputChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {Object.values(ExperienceLevel).map(level => (
              <option key={level} value={level}>
                {level.charAt(0) + level.slice(1).toLowerCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Current Job Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Job Title (Optional)
          </label>
          <input
            type="text"
            name="currentJobTitle"
            value={formData.currentJobTitle}
            onChange={handleInputChange}
            placeholder="e.g., Junior Software Developer"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Current Company */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Company (Optional)
          </label>
          <input
            type="text"
            name="currentCompany"
            value={formData.currentCompany}
            onChange={handleInputChange}
            placeholder="e.g., Startup Inc"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Current Skills */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Skills * (Press Enter to add)
          </label>
          <input
            type="text"
            value={skillInput}
            onChange={(e) => setSkillInput(e.target.value)}
            onKeyDown={(e) => handleAddItem(e, skillInput, setSkillInput, 'skills')}
            placeholder="e.g., HTML, CSS, Python"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.skills ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.skills.map(skill => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(skill, 'skills')}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors.skills && <p className="mt-1 text-sm text-red-600">{errors.skills}</p>}
        </div>

        {/* Desired Skills */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Skills You Want to Learn * (Press Enter to add)
          </label>
          <input
            type="text"
            value={desiredSkillInput}
            onChange={(e) => setDesiredSkillInput(e.target.value)}
            onKeyDown={(e) => handleAddItem(e, desiredSkillInput, setDesiredSkillInput, 'desiredSkills')}
            placeholder="e.g., React, Machine Learning, Leadership"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.desiredSkills ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.desiredSkills.map(skill => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
              >
                {skill}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(skill, 'desiredSkills')}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors.desiredSkills && <p className="mt-1 text-sm text-red-600">{errors.desiredSkills}</p>}
        </div>

        {/* Industries */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Industries of Interest (Press Enter to add)
          </label>
          <input
            type="text"
            value={industryInput}
            onChange={(e) => setIndustryInput(e.target.value)}
            onKeyDown={(e) => handleAddItem(e, industryInput, setIndustryInput, 'industries')}
            placeholder="e.g., Technology, Finance, Healthcare"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.industries.map(industry => (
              <span
                key={industry}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-700"
              >
                {industry}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(industry, 'industries')}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Career Goals */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Career Goals * (Press Enter to add)
          </label>
          <input
            type="text"
            value={goalInput}
            onChange={(e) => setGoalInput(e.target.value)}
            onKeyDown={(e) => handleAddItem(e, goalInput, setGoalInput, 'careerGoals')}
            placeholder="e.g., Become a Senior Developer, Start a company"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.careerGoals ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.careerGoals.map(goal => (
              <span
                key={goal}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800"
              >
                {goal}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(goal, 'careerGoals')}
                  className="ml-2 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          {errors.careerGoals && <p className="mt-1 text-sm text-red-600">{errors.careerGoals}</p>}
        </div>

        {/* Challenges */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Challenges (Optional, Press Enter to add)
          </label>
          <input
            type="text"
            value={challengeInput}
            onChange={(e) => setChallengeInput(e.target.value)}
            onKeyDown={(e) => handleAddItem(e, challengeInput, setChallengeInput, 'challenges')}
            placeholder="e.g., Time management, Technical interviews"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {formData.challenges?.map(challenge => (
              <span
                key={challenge}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-800"
              >
                {challenge}
                <button
                  type="button"
                  onClick={() => handleRemoveItem(challenge, 'challenges')}
                  className="ml-2 text-red-600 hover:text-red-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleInputChange}
            placeholder="e.g., New York, NY"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.location ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location}</p>}
        </div>

        {/* Bio */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bio * (Tell mentors about yourself)
          </label>
          <textarea
            name="bio"
            value={formData.bio}
            onChange={handleInputChange}
            rows={4}
            placeholder="Share your background, what you're working on, and what you hope to achieve..."
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.bio ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.bio && <p className="mt-1 text-sm text-red-600">{errors.bio}</p>}
        </div>

        {/* Learning Style */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Learning Style (Optional)
          </label>
          <textarea
            name="learningStyle"
            value={formData.learningStyle}
            onChange={handleInputChange}
            rows={2}
            placeholder="How do you learn best? (e.g., hands-on projects, structured lessons, etc.)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Looking for Mentor */}
        <div className="flex items-center">
          <input
            type="checkbox"
            name="isLookingForMentor"
            checked={formData.isLookingForMentor}
            onChange={handleInputChange}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <label className="ml-2 text-sm text-gray-700">
            I am actively looking for a mentor
          </label>
        </div>

        {/* Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Back
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-primary-500 text-white rounded-lg font-bold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Completing...' : 'Complete Profile'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MenteeOnboardingForm;
